import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

// @mui
import { styled } from '@mui/material/styles';
import { Typography, Box, Button, Switch, Tooltip } from '@mui/material';

import pageTitle from "../utils/pageTitles.json";
import { BlankCard } from '../components/cards/blankCard';
import UsersListSvg from '../assets/svgs/usersList';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { checkButtonAccess } from 'src/utils/common';
import { SmsRealNumberTable } from 'src/sections/sms/mockNumber/smsRealNumberTable';
import AddSmsRealNumber from 'src/sections/sms/mockNumber/addSmsRealNumber';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';


// home main section container.
const MainSection = styled('div')(({ theme }) => ({
    padding: "64px 20px 0px 88px",
    backgroundColor: "rgb(240, 240, 240)"/* theme.palette.background.default */,
    height: '100vh',
    overflow: 'hidden'
}));

// Show home hading and filtes
const HeadingSection = styled('div')(() => ({
    width: '100%',
    padding: "20px 0px 20px 0px",
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row'
}));


const HeadingContentSection = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '15px',
    alignItems: 'center',
    justifyContent: 'center',
}));

const HeadingActions = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginRight: '20px',
    flexShrink: 0,
}));

const SwitchButton = styled(Switch)(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#65C466',
                opacity: 1,
                border: 0,
            },
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 13,
        backgroundColor: theme.palette.grey[500],
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 300,
        }),
    },
}));

const DetailsListSection = styled('div')(() => ({
    width: '100%',
    height: 'calc(100vh - 155px)',//'554px',
    overflow: 'hidden',
    overflowY: "scroll",
}));


const SmsRealNumber= () => {
    const [openModal, setOpenModal] = useState(false);
    const [loadit, forceLoad] = useState(false);
    const [updateUserId, setUpdateUserId] = useState('');
    const [universalMock, setUniversalMock] = useState(false);
    const [isMockSettingLoading, setIsMockSettingLoading] = useState(true);
    const [isMockSettingUpdating, setIsMockSettingUpdating] = useState(false);

    const canUpdateSmsSettings = checkButtonAccess("smsRealNumber", "w");
    const canUpdatemockSettings = checkButtonAccess("testingMockNumbers", "w");

    const handleClick = () => {
        setOpenModal(true);
    }
    const handleCloseModal = () => {
        setOpenModal(false);
        setUpdateUserId('');
        
    };

    useEffect(() => {
        if (updateUserId) {
            setOpenModal(true);
        }
    }, [updateUserId]);

    useEffect(() => {
        const getUniversalMockSetting = async () => {
            try {
                const result = await axiosGetCall('getUnivarsalMockSetting');
                const status = result?.univarsal_mock;
                setUniversalMock(status === true || status === 1 || status === '1' || status === 'true');
            } catch (error) {
                renderErrorMessage(error?.message);
            } finally {
                setIsMockSettingLoading(false);
            }
        };

        getUniversalMockSetting();
    }, []);

    const handleUniversalMockChange = async (event) => {
        const previousStatus = universalMock;
        const status = event.target.checked;

        setUniversalMock(status);
        setIsMockSettingUpdating(true);

        try {
            const result = await axiosPostCall('updateUnivarsalMockSetting', { status });
            renderSuccessMessage(result?.message || 'Universal mock setting updated successfully');
        } catch (error) {
            setUniversalMock(previousStatus);
            renderErrorMessage(error?.message);
        } finally {
            setIsMockSettingUpdating(false);
        }
    };


 

    return (
        <>
            <Helmet>
                <title> {`${pageTitle?.smsRealNumber}`} </title>
            </Helmet>

            <MainSection>

                {/* Heading and filters */}
                <HeadingSection>
                    <HeadingContentSection>
                        <Box sx={{
                            widht: '16px',
                            height: '16px',
                            mt: "-10px",
                        }}>
                            <UsersListSvg />
                        </Box>
                        <Typography variant='h5' sx={{ color: 'grey.10', fontWeight: 600 }}>
                            SMS Real Number
                        </Typography>
                    </HeadingContentSection>
                    <HeadingActions>
                        {checkButtonAccess("testingMockNumbers", "r") && (
                            <Tooltip
                                title="When Universal Mock is enabled, it applies to all users. Real SMS messages are sent only to the real numbers added here for testing, and all other users will not receive real SMS messages. Users can also enable or disable their individual SMS Mock setting, but Universal Mock overrides that setting. Therefore, when Universal Mock is active, real SMS messages will not be sent even if an individual user's SMS Mock setting is off. Disable Universal Mock to resume normal SMS delivery."
                                arrow
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Typography sx={{ color: 'grey.700', fontWeight: 500 }}>
                                        Universal Mock
                                    </Typography>
                                    <SwitchButton
                                        checked={universalMock}
                                        onChange={handleUniversalMockChange}
                                        disabled={
                                            !canUpdatemockSettings ||
                                            isMockSettingLoading ||
                                            isMockSettingUpdating
                                        }
                                        inputProps={{ 'aria-label': 'Universal mock setting' }}
                                    />
                                    <Typography
                                        sx={{
                                            color: universalMock ? 'success.main' : 'text.secondary',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            minWidth: '70px',
                                        }}
                                    >
                                        {isMockSettingLoading
                                            ? 'Loading...'
                                            : universalMock
                                                ? 'Active'
                                                : 'Not Active'}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                        {canUpdateSmsSettings && <Button
                            startIcon={<PersonAddIcon />}
                            sx={{
                                backgroundColor: 'rgb(33, 43, 54)',
                                color: 'grey.100',
                                p: '0px 20px 0px 20px',
                                '&:hover': {
                                    backgroundColor: 'rgb(69, 79, 91)'
                                }
                            }}
                            onClick={() => handleClick()}
                        >
                            Add Number
                        </Button>}
                    </HeadingActions>
                </HeadingSection>

                <BlankCard
                    key="1"
                    sx={{
                        height: '100%',
                        width: '100%',
                        padding: '0px',
                        overflow: 'hidden',
                        // borderTopLeftRadius: '0px',
                        // borderTopRightRadius: '0px'
                    }}
                >
                    <DetailsListSection className='reportListScroll'>
                        <SmsRealNumberTable loadit={loadit}  setUpdateUserId={setUpdateUserId} forceLoad={forceLoad}/>
                        </DetailsListSection>
                </BlankCard> 

            </MainSection>
            <AddSmsRealNumber
                openModal={openModal}
                handleCloseModal={handleCloseModal}
                forceLoad={forceLoad}
            />
           
        </>
    )
}


export default SmsRealNumber;
